import React from 'react';
import tenantUtils from '@utils';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { notification } from '../../../../components/common';
import { useGetAccountManagerInfoQuery } from '../../../../apis/common';
import { adLicenseAssistanceRequestedEvent } from '../../../../services/analyticsService';
import { useRequestRegaAdLicenseAssistanceMutation } from '../../apis/regaAdLicenses';
import { formatNumberString } from '../../../../utility/utility';
import { AssistListIcon } from './icons';
import { v2Color } from '@theme/v2';

const boldStyle = { color: v2Color('#272B41', 'text'), fontWeight: 700 };

/**
 * Shared body + footer for the Request Assistance flow, reused by:
 *  - the standalone dashboard modal (no Back), and
 *  - the list modal's in-place assistance view (with a Back header owned by the list modal).
 * onCancel runs on Cancel; onSent runs after a successful request.
 */
const RequestAssistanceContent = ({
  unpostedCount = 0,
  onCancel,
  onSent,
  bodyPadding = '20px 4px 28px',
  footerPadding = '14px 24px',
}) => {
  const { t } = useTranslation();
  const { user: loginUser } = useSelector((state) => state.app.loginUser);

  // Existing Profolio endpoint: GET /api/surge/dashboard/account_manager (via apis/common).
  const { data: amData } = useGetAccountManagerInfoQuery(loginUser, { skip: !loginUser });
  const accountManagerName = tenantUtils.getLocalisedString(amData?.account_manager?.assignee, 'name');

  const [requestAssistance, { isLoading }] = useRequestRegaAdLicenseAssistanceMutation();

  const handleSend = async () => {
    try {
      await requestAssistance().unwrap();
      adLicenseAssistanceRequestedEvent(loginUser, { status: 'success', value: unpostedCount });
      notification.success(t('Your request has been shared. Someone from Bayut will reach out to you.'));
      onSent();
    } catch (error) {
      adLicenseAssistanceRequestedEvent(loginUser, { status: 'error', value: unpostedCount });
      const message = typeof error === 'string' ? error : error?.message || error?.error;
      notification.error(message || t('Something went wrong'));
    }
  };

  const countText = formatNumberString(unpostedCount);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', height: '100%', minHeight: 0 }}>
      <div style={{ flex: '1 1 auto', display: 'grid', placeItems: 'center', padding: bodyPadding }}>
        <div style={{ display: 'grid', gap: '20px', maxWidth: '560px', justifyItems: 'center', textAlign: 'center' }}>
          <span
            style={{
              display: 'grid',
              placeContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: v2Color('#F2FAFA'),
              color: v2Color('#006169', 'text'),
            }}
          >
            <AssistListIcon size={24} />
          </span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'rgba(0,0,0,0.88)' }}>{t('Request assistance?')}</span>
          <span style={{ fontSize: '14px', color: 'rgb(112,112,112)', lineHeight: 1.7 }}>
            {accountManagerName ? (
              <Trans
                i18nKey="Your account manager <b>{{name}}</b> will reach out to you to discuss posting your <b>{{count}}</b> unposted ad licenses."
                values={{ name: accountManagerName, count: countText }}
                components={{ b: <strong style={boldStyle} /> }}
              />
            ) : (
              <Trans
                i18nKey="Your Account Manager will reach out to you to discuss posting your <b>{{count}}</b> unposted ad licenses."
                values={{ count: countText }}
                components={{ b: <strong style={boldStyle} /> }}
              />
            )}
          </span>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: footerPadding,
          borderBlockStart: '1px solid #f0f0f0',
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px',
            padding: '0 20px',
            background: '#fff',
            color: v2Color('#006169', 'text'),
            border: `1px solid ${v2Color('#A6C8CA')}`,
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {t('Cancel')}
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={isLoading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px',
            padding: '0 24px',
            background: v2Color('#006169'),
            color: '#fff',
            border: `1px solid ${v2Color('#006169')}`,
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            opacity: isLoading ? 0.75 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? `${t('Send request')}…` : t('Send request')}
        </button>
      </div>
    </div>
  );
};

export default RequestAssistanceContent;
